import React, {useRef, useState} from "react";
import {Alert, AutoComplete, Button, Col, Drawer, Form, Input, Row, Select, Switch, message} from "antd";
import {ApiLinksService} from "../../../../../shared/api";
import {PORTAL_PAGES, UTM_FIELDS, errorOf, previewUrl} from "./linkParts";

const OWN_PATH = 'own'
const FULL_URL = /^https?:\/\//i

// a new link: the site and its page, UTM, a short form if the site allows it
export const LinkForm = ({domains, onClose, onCreated}) => {
  const [form] = Form.useForm()
  const [saving, setSaving] = useState(false)
  const [resolving, setResolving] = useState(false)
  const taken = useRef('')
  // the fields the form and the preview follow (Form.useWatch of antd 4.20 watches one field at a time)
  const values = {
    domainId: Form.useWatch('domainId', form),
    page: Form.useWatch('page', form),
    path: Form.useWatch('path', form),
    short: Form.useWatch('short', form),
    utm_source: Form.useWatch('utm_source', form),
    utm_medium: Form.useWatch('utm_medium', form),
    utm_campaign: Form.useWatch('utm_campaign', form),
    utm_content: Form.useWatch('utm_content', form),
    utm_term: Form.useWatch('utm_term', form),
  }
  const domain = domains.find((d) => d.id === values.domainId)
  const isPortal = domain && domain.host === 'portalagentam.ru'
  const path = isPortal && values.page && values.page !== OWN_PATH ? values.page : values.path
  const preview = domain ? previewUrl(domain.host, path || '/', values) : ''

  // a link as the manager has it, the short one too: the server finds the site and the page
  const takeAddress = async (value, field = 'address') => {
    const url = (value || '').trim()
    form.setFields([{name: field, errors: []}])
    if (!url || url === taken.current) return
    taken.current = url
    setResolving(true)
    try {
      const {data} = await ApiLinksService.resolve(url)
      const site = domains.find((d) => d.id === data.domainId)
      form.setFieldsValue({
        domainId: data.domainId,
        path: data.path,
        ...(site && site.host === 'portalagentam.ru' ? {page: OWN_PATH} : {}),
        ...(data.domainId !== values.domainId ? {short: false} : {}),
      })
    } catch (error) {
      taken.current = ''
      form.setFields([{name: field, errors: [errorOf(error).message || 'Не удалось разобрать ссылку']}])
    }
    setResolving(false)
  }

  const submit = async () => {
    let v
    try {
      v = await form.validateFields()
    } catch (e) {
      return
    }
    setSaving(true)
    try {
      const body = {
        title: v.title,
        description: v.description || null,
        domainId: v.domainId,
        path: path || '/',
        ...Object.fromEntries(UTM_FIELDS.map(({key}) => [key, (v[key] || '').trim()])),
        short: Boolean(v.short),
        slug: v.short ? (v.slug || '').trim() : undefined,
      }
      const {data} = await ApiLinksService.create(body)
      message.success('Ссылка готова')
      onCreated(data)
    } catch (error) {
      const {message: text, field} = errorOf(error)
      const known = ['title', 'description', 'domainId', 'path', 'slug', 'short', ...UTM_FIELDS.map((f) => f.key)]
      if (field && known.includes(field)) form.setFields([{name: field === 'path' && isPortal ? 'page' : field, errors: [text]}])
      else message.error(text || 'Не удалось создать ссылку')
    }
    setSaving(false)
  }

  return (
    <Drawer
      visible
      width={Math.min(760, window.innerWidth)}
      title='Новая ссылка'
      onClose={onClose}
      destroyOnClose
      footer={(
        <div style={{textAlign: 'right'}}>
          <Button onClick={onClose} style={{marginRight: 8}}>Отмена</Button>
          <Button type='primary' loading={saving || resolving} onClick={submit}>Создать</Button>
        </div>
      )}
    >
      <Form form={form} layout='vertical' initialValues={{domainId: domains[0] && domains[0].id, path: '/', page: '/login', short: false}}>
        <Form.Item name='title' label='Название' rules={[{required: true, whitespace: true, message: 'Назовите ссылку'}]}
          extra='Для себя: «Рассылка 05.10 — осенние скидки, Telegram»'>
          <Input maxLength={200}/>
        </Form.Item>
        <Form.Item name='description' label='Описание'
          extra='Например, текст рассылки, с которым ушла ссылка. Его можно поменять и потом — на статистику не влияет'>
          <Input.TextArea maxLength={4000} autoSize={{minRows: 3, maxRows: 10}}/>
        </Form.Item>
        <Form.Item name='address' label='Ссылка на подборку или страницу'
          extra='Вставьте ссылку как есть, короткую тоже. Сайт и страница подставятся сами'>
          <Input placeholder='https://…' allowClear
            onPaste={(e) => takeAddress(e.clipboardData.getData('text'))}
            onBlur={(e) => takeAddress(e.target.value)}/>
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={10}>
            <Form.Item name='domainId' label='Сайт' rules={[{required: true, message: 'Выберите сайт'}]}>
              <Select options={domains.map((d) => ({value: d.id, label: d.title ? `${d.host} — ${d.title}` : d.host}))}
                onChange={() => form.setFieldsValue({short: false})}/>
            </Form.Item>
          </Col>
          <Col xs={24} md={14}>
            {isPortal ? (
              <Form.Item name='page' label='Страница'>
                <Select options={[...PORTAL_PAGES, {value: OWN_PATH, label: 'Свой адрес на портале…'}]}/>
              </Form.Item>
            ) : (
              <Form.Item name='path' label='Страница' extra='Адрес на сайте после домена: / — главная'>
                <Input placeholder='/' onBlur={(e) => FULL_URL.test(e.target.value) && takeAddress(e.target.value, 'path')}/>
              </Form.Item>
            )}
            {isPortal && values.page === OWN_PATH && (
              <Form.Item name='path' label='Адрес на портале' extra='Например /residential-complexes/123/housings'>
                <Input placeholder='/' onBlur={(e) => FULL_URL.test(e.target.value) && takeAddress(e.target.value, 'path')}/>
              </Form.Item>
            )}
          </Col>
        </Row>

        <Row gutter={16}>
          {UTM_FIELDS.map((f) => (
            <Col xs={24} md={f.key === 'utm_term' ? 24 : 12} key={f.key}>
              <Form.Item
                name={f.key}
                label={`${f.label} (${f.key})`}
                rules={[
                  ...(f.required ? [{required: true, whitespace: true, message: 'Обязательное поле'}] : []),
                  {pattern: /^[A-Za-z0-9._-]*$/, message: 'Латиница, цифры, точка, дефис и подчёркивание'},
                ]}
              >
                <AutoComplete options={f.options.map((o) => ({value: o}))} placeholder={f.placeholder}
                  filterOption={(input, option) => option.value.startsWith(input.toLowerCase())}/>
              </Form.Item>
            </Col>
          ))}
        </Row>

        <Form.Item name='short' label='Короткая ссылка' valuePropName='checked'
          extra={domain && !domain.short_links
            ? `Для ${domain.host} выключены (вкладка «Сайты»): ссылка ведёт на сайт напрямую`
            : `${((domain && domain.short_base) || 'https://portalagentam.ru/s').replace('https://', '')}/… — переход через наш сервер, он считает клики`}>
          <Switch disabled={!domain || !domain.short_links}/>
        </Form.Item>
        {values.short && (
          <Form.Item name='slug' label='Своё короткое имя'
            extra='Необязательно: пусто — придумаем сами. 3–40 символов: латиница, цифры, дефис'
            rules={[{pattern: /^[A-Za-z0-9-]*$/, message: 'Латиница, цифры и дефис'}]}>
            <Input addonBefore={`${((domain && domain.short_base) || 'https://portalagentam.ru/s').replace('https://', '')}/`} placeholder='osen-2026' maxLength={40}/>
          </Form.Item>
        )}
      </Form>
      {preview && <Alert type='info' message='Длинная ссылка' description={<span className='link-preview'>{preview}</span>}/>}
    </Drawer>
  )
}
